import React, { useLayoutEffect, useState } from "react";
import { StyleSheet, Text, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { UserCard, ReviewCarousel, Button, ActionDialog } from "src/components";
import { Rating } from "src/components/offer-list-item-large/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { stringDateFormat } from "src/utils/date";
import { useUser, useReviewByUserAsUser, useBlockActions } from "src/hooks";
import LocalText from "src/local";
import alert from "src/components/alert";
import { RootStackScreenProps } from "src/navigation/types";

export type UserNavigationParam = {
  id: string;
  title: string;
  hideStatus?: boolean;
};

export const UserScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"User">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"User">["route"]>();
  const [user] = useUser(route.params.id);
  const [reviewByUserAsUserItems] = useReviewByUserAsUser(route.params.id);
  const [moreDialogOpen, setMoreDialogOpen] = useState<boolean>(false);
  const { block } = useBlockActions();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: route.params.title,
      headerRight: () => (
        <Button
          onPress={() => {
            setMoreDialogOpen(true);
          }}
          variant={"icon"}
          icon={"more-circle"}
          iconSize={24}
          iconColor={colors.chokuRed}
          containerStyle={{ marginRight: 16 }}
        />
      ),
    });
  }, [navigation, route.params.title]);

  const moreActionSheetHandler = async (index: number) => {
    if (index === 0) {
      await block(route.params.id);
      alert(
        LocalText.screens.user.blockAlertTitle,
        undefined,
        [
          {
            text: LocalText.common.ok,
            style: "cancel",
            onPress: () => navigation.goBack(),
          },
        ],
        { cancelable: false }
      );
    }
  };

  return (
    <View style={styles.container}>
      {user ? (
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          <UserCard
            contanerStyle={styles.owner}
            source={user?.image ? { uri: user?.image } : undefined}
            status={route.params.hideStatus ? undefined : user?.status}
            name={user?.name}
            birthday={user?.birthday}
            gender={user?.gender}
            prefecture={user?.address?.prefecture}
            isVerified={user?.identificationStatus === "APPROVED"}
          />
          <View style={styles.row}>
            <Rating value={user.rate || 0} count={user.rateCount || 0} />
            <Text style={styles.createdAt}>
              {LocalText.screens.user.createdAt}
              {stringDateFormat(user.createdAt || "")}
            </Text>
          </View>
          {user.aboutTalk ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.user.aboutTalk}
              </Text>
              <Text style={styles.text}>{user.aboutTalk}</Text>
            </React.Fragment>
          ) : null}
          {user.aboutProfile ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.user.aboutProfile}
              </Text>
              <Text style={styles.text}>{user.aboutProfile}</Text>
            </React.Fragment>
          ) : null}
          {user.description ? (
            <React.Fragment>
              <Text style={styles.section}>
                {LocalText.screens.user.description}
              </Text>
              <Text style={styles.text}>{user.description}</Text>
            </React.Fragment>
          ) : null}
          {reviewByUserAsUserItems.length > 0 ? (
            <ReviewCarousel
              title={LocalText.screens.user.review}
              reviews={reviewByUserAsUserItems}
              onMore={() =>
                navigation.push("ReviewByUserAsUser", {
                  id: route.params.id,
                  title: LocalText.screens.user.review,
                  notFound: LocalText.screens.user.reviewNotFound,
                })
              }
            />
          ) : null}
        </ScrollView>
      ) : null}
      <ActionDialog
        open={moreDialogOpen}
        onClose={() => setMoreDialogOpen(false)}
        onAction={(index) => {
          setMoreDialogOpen(false);
          moreActionSheetHandler(index);
        }}
        items={[LocalText.screens.user.block].map((key) => ({
          key,
          label: key,
        }))}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  createdAt: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.chokuGray5,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  section: {
    fontSize: 12,
    lineHeight: 18,
    color: colors.chokuGray,
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  text: {
    fontSize: 12,
    lineHeight: 18,
    color: "#333333",
    paddingTop: 8,
    paddingHorizontal: 16,
  },
  owner: {
    marginTop: 16,
    marginHorizontal: 16,
    marginBottom: 8,
  },
});
